import React, { FC, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Collapse,
    FormCheck,
    Modal
} from "react-bootstrap";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
    ADITOR_GET_SCENES,
    ADITOR_INIT_QUERY,
    ADITOR_LOAD_SCENE
} from "../../../graphql/queries";
import {
    AditorInitQueryData,
    AditorLoadSceneData,
    AditorSceneData,
    AditorScenesData,
    AditorSceneVariables,
    AditorUpdateData,
    AditorUpdateVariables,
    UpdateAditorSceneDetailsData
} from "../../../models/aditor";
import { AditorHistoryElement } from "../../simple/aditor/AditorHistoryElement";
import { ADITOR_UPDATE_SCENE_DETAILS } from "../../../graphql/mutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faSave } from "@fortawesome/free-solid-svg-icons";

interface Props {
    modalConfig: {
        show: boolean;
        focus: string | undefined;
    };
    revisionId: number;
    revisionName: string;
    currentSceneHash?: string;
    setCurrentScene: (scene: AditorSceneData) => void;
    updateScene: (updateMessage: AditorUpdateData) => void;
    deleteScene: (sceneId: number) => void;
    updateCurrentSceneTag: (tag: string) => void;
    onClose: () => void;
}

export const AditorSnapshotModal: FC<Props> = ({
    modalConfig,
    revisionId,
    revisionName,
    currentSceneHash,
    setCurrentScene,
    deleteScene,
    updateCurrentSceneTag,
    onClose
}) => {
    const client = useApolloClient();
    const [showInfo, updateShowInfo] = useState(false);
    const [showToBeRemoved, updateShowToBeRemoved] = useState(false);
    const { data } = useQuery<AditorScenesData, AditorSceneVariables>(
        ADITOR_GET_SCENES,
        {
            variables: {
                revisionId: Number(revisionId)
            }
        }
    );
    const [updateSceneDetailsMutation] = useMutation<
        UpdateAditorSceneDetailsData,
        AditorUpdateVariables
    >(ADITOR_UPDATE_SCENE_DETAILS);

    const updateSceneDetails = async (
        sceneId: number,
        tag: string,
        hash: string
    ) => {
        try {
            const results = await updateSceneDetailsMutation({
                variables: {
                    input: {
                        revisionId: Number(revisionId),
                        sceneId: Number(sceneId),
                        tag: tag
                    }
                }
            });
            const currentCache = client.readQuery<AditorInitQueryData>({
                query: ADITOR_INIT_QUERY,
                variables: {
                    revisionId: revisionId
                }
            });
            if (currentCache && currentCache.aditorLoadScene) {
                if (
                    Number(currentCache.aditorLoadScene.id) ===
                    Number(results.data?.aditorUpdateSceneDetails.id)
                ) {
                    client.writeQuery<AditorInitQueryData>({
                        query: ADITOR_INIT_QUERY,
                        data: {
                            ...currentCache,
                            aditorLoadScene: {
                                ...currentCache.aditorLoadScene,
                                tag: tag
                            }
                        },
                        variables: {
                            revisionId: revisionId
                        }
                    });
                }
            }
            const loadSceneCache = client.readQuery<AditorLoadSceneData>({
                query: ADITOR_LOAD_SCENE,
                variables: {
                    revisionId: Number(revisionId),
                    sceneId: Number(sceneId)
                }
            });
            if (loadSceneCache && loadSceneCache.aditorLoadScene) {
                client.writeQuery<AditorLoadSceneData>({
                    query: ADITOR_LOAD_SCENE,
                    data: {
                        aditorLoadScene: {
                            ...loadSceneCache.aditorLoadScene,
                            tag: tag
                        }
                    },
                    variables: {
                        revisionId: revisionId,
                        sceneId: sceneId
                    }
                });
            }
            if (currentSceneHash === hash) {
                updateCurrentSceneTag(tag);
            }
        } catch (e) {
            console.log("[DEBUG] error ", e);
        }
    };
    const latestHash: string | undefined =
        data?.aditorScenes[0]?.hash || undefined;
    const tagElements = data?.aditorScenes
        .filter(scene => scene.tag)
        .map((aditorScene: AditorSceneData, index: number) => (
            <AditorHistoryElement
                key={index}
                aditorScene={aditorScene}
                updateSceneDetails={updateSceneDetails}
                loadScene={() => {
                    setCurrentScene(aditorScene);
                    onClose();
                }}
                deleteScene={() => deleteScene(Number(aditorScene.id))}
                currentSceneHash={currentSceneHash}
                latestHash={latestHash}
                focusAtStart={
                    modalConfig.focus !== undefined &&
                    (modalConfig.focus === aditorScene.id.toString() ||
                        modalConfig.focus === aditorScene.tag)
                }
            />
        ));

    const canAddTag = !(tagElements && tagElements.length >= 5);
    const historyElements = data?.aditorScenes
        .filter(scene => {
            if (scene.tag) {
                return false;
            }
            return !(!showToBeRemoved && scene.expirationDate);
        })
        .map((aditorScene: AditorSceneData, index: number) => (
            <AditorHistoryElement
                key={index}
                aditorScene={aditorScene}
                updateSceneDetails={updateSceneDetails}
                loadScene={() => {
                    setCurrentScene(aditorScene);
                    onClose();
                }}
                deleteScene={() => deleteScene(Number(aditorScene.id))}
                currentSceneHash={currentSceneHash}
                latestHash={latestHash}
                focusAtStart={
                    modalConfig.focus !== undefined &&
                    (modalConfig.focus === aditorScene.id.toString() ||
                        modalConfig.focus === aditorScene.tag)
                }
                canAddTag={canAddTag}
            />
        ));
    return (
        <Modal
            show={modalConfig.show}
            onHide={onClose}
            backdrop="static"
            size="lg"
            onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
            <Modal.Header closeButton>
                <h3>Save History - {revisionName}</h3>
            </Modal.Header>
            <Modal.Body className="p-2">
                <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                        <h5>Save History</h5>{" "}
                        <FontAwesomeIcon
                            icon={faInfoCircle}
                            title="Toggle info"
                            onClick={() => updateShowInfo(!showInfo)}
                            className="text-end ms-auto text-secondary mouseHover"
                        />
                    </Card.Header>
                    <Card.Body>
                        <Collapse in={showInfo}>
                            <div className="bread-text text-secondary p-2 pt-0">
                                <p>
                                    {" "}
                                    Save History shows five (5) latest saves
                                    made to the scene that is currently open in
                                    Aditor.
                                </p>
                                <p>
                                    Move saves to <i>Stored Saves</i> by giving
                                    them identifier tags. Those saves will
                                    persist until deleted.
                                </p>
                                <Alert variant="danger">
                                    Latest saved version is used in the next
                                    build!
                                </Alert>
                            </div>
                        </Collapse>
                        <FormCheck
                            type="switch"
                            id="showToBeRemoved"
                            label="Show to-be-removed saves"
                            checked={showToBeRemoved}
                            onChange={() =>
                                updateShowToBeRemoved(!showToBeRemoved)
                            }
                        />
                        {historyElements && historyElements?.length > 0 ? (
                            <>{historyElements}</>
                        ) : (
                            <p className="bread-text text-secondary">
                                No save history.
                            </p>
                        )}
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header className="d-flex align-items-center">
                        <h5>Stored Saves</h5>{" "}
                        <FontAwesomeIcon
                            icon={faSave}
                            size="1x"
                            className="ms-2 ps-1 mb-2"
                        />
                    </Card.Header>
                    <Card.Body>
                        {tagElements && tagElements?.length > 0 ? (
                            <>{tagElements}</>
                        ) : (
                            <p className="bread-text text-secondary">
                                No manually stored saves.
                            </p>
                        )}
                    </Card.Body>
                </Card>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center pt-0 mb-1">
                <Button
                    className="btn-secondary w-50"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
